<script setup>
/* eslint-disable no-use-before-define */
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import SendAppointmentButton from './SendAppointmentButton.vue';
import TalkviaCalendarEventsAPI from 'dashboard/api/talkviaCalendarEvents';
import {
  calendarDateKey,
  calendarFormat,
  shiftCalendarDays,
} from '../calendar/calendarTime';

const props = defineProps({
  conversation: { type: Object, default: () => ({}) },
  contact: {
    type: Object,
    default: null,
  },
  conversationId: {
    type: [Number, String],
    default: '',
  },
});

const route = useRoute();

// Próximos compromissos do contato na agenda da Talkvia (inclui os marcados pela
// IA e os retornos de conversas adiadas). Só leitura: não importa do Google aqui.
const APPOINTMENT_WINDOW_DAYS = 180;
const MAX_APPOINTMENTS = 3;
const appointments = ref([]);
let appointmentsRequest = 0;

const loadAppointments = async contactId => {
  appointmentsRequest += 1;
  const request = appointmentsRequest;
  appointments.value = [];
  if (!contactId) return;

  const now = new Date();
  const until = new Date(now.getTime() + APPOINTMENT_WINDOW_DAYS * 86400000);
  try {
    const { data } = await TalkviaCalendarEventsAPI.get({
      contact_id: contactId,
      status: 'scheduled',
      start_at: now.toISOString(),
      end_at: until.toISOString(),
      skip_sync: true,
    });
    if (request !== appointmentsRequest) return;
    appointments.value = (Array.isArray(data) ? data : [])
      .filter(event => new Date(event.ends_at || event.starts_at) >= now)
      .sort((a, b) => new Date(a.starts_at) - new Date(b.starts_at));
  } catch {
    // Sem agenda disponível, o bloco simplesmente não aparece.
  }
};

watch(
  () => props.contact?.id,
  contactId => loadAppointments(contactId),
  { immediate: true }
);

const visibleAppointments = computed(() =>
  appointments.value.slice(0, MAX_APPOINTMENTS)
);
const hiddenAppointments = computed(() =>
  Math.max(appointments.value.length - MAX_APPOINTMENTS, 0)
);
const agendaUrl = computed(
  () => `/app/accounts/${route.params.accountId}/agenda`
);

const APPOINTMENT_SOURCES = {
  ai: 'Agendado pela IA',
  snooze: 'Retorno da conversa',
  google: 'Google Agenda',
  proposal: 'Proposta',
  external: 'Sistema externo',
  manual: 'Agenda',
};

const appointmentSource = event =>
  APPOINTMENT_SOURCES[event.source] || APPOINTMENT_SOURCES.manual;

const appointmentDay = event => {
  const day = calendarDateKey(event.starts_at);
  const today = new Date();
  if (day === calendarDateKey(today)) return 'Hoje';
  if (day === calendarDateKey(shiftCalendarDays(today, 1))) return 'Amanhã';
  return calendarFormat(event.starts_at, {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
  });
};

const appointmentTime = event => {
  if (event.all_day) return 'Dia inteiro';
  const format = value =>
    calendarFormat(value, {
      hour: '2-digit',
      minute: '2-digit',
    });
  return event.ends_at
    ? `${format(event.starts_at)}–${format(event.ends_at)}`
    : format(event.starts_at);
};

const profile = computed(() => props.contact?.talkvia_profile || {});
const proposals = computed(() => props.contact?.talkvia_proposals || {});
const latestProposals = computed(() =>
  Array.isArray(proposals.value.latest) ? proposals.value.latest : []
);

const primaryProposal = computed(() => latestProposals.value[0] || null);

// O CPF já aparece no contexto rápido e na identidade do contato, no topo da coluna:
// repeti-lo aqui gastava um terço da faixa sem acrescentar informação.
const contextCards = computed(() => [
  {
    label: 'Margem',
    value: formatCurrency(profile.value.available_margin),
    icon: 'i-lucide-wallet-cards',
  },
  {
    label: 'Propostas',
    value: `${proposals.value.total || 0}`,
    icon: 'i-lucide-file-pen-line',
  },
]);

const timeline = computed(() => {
  const items = [];

  if (props.contact?.created_at) {
    items.push({
      label: 'Cliente entrou na base',
      date: props.contact.created_at,
      icon: 'i-lucide-user-plus',
    });
  }

  if (profile.value.last_interaction_at) {
    items.push({
      label: 'Última interação registrada',
      date: profile.value.last_interaction_at,
      icon: 'i-lucide-message-circle',
    });
  }

  latestProposals.value.forEach(proposal => {
    items.push({
      label: `${statusLabel(proposal.status)} · ${proposal.title || 'Proposta'}`,
      date: proposal.updated_at,
      amount: proposal.amount,
      icon: 'i-lucide-file-text',
    });
  });

  return items
    .filter(item => item.date)
    .sort((a, b) => new Date(b.date) - new Date(a.date))
    .slice(0, 5);
});

const pipelineUrl = computed(
  () => `/app/accounts/${route.params.accountId}/propostas`
);

const conversationUrl = computed(
  () =>
    `/app/accounts/${route.params.accountId}/conversations/${props.conversationId}`
);

function formatCurrency(value) {
  const parsed = Number(value || 0);
  if (!parsed) return 'Sem valor';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  }).format(parsed);
}

function formatDate(value) {
  if (!value) return '';
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
}

function statusLabel(status) {
  return (
    {
      em_andamento: 'Em andamento',
      aguardando_cliente: 'Aguardando cliente',
      aguardando_banco: 'Aguardando banco',
      aguardando_assinatura: 'Aguardando assinatura',
      aprovado: 'Aprovada',
      pago: 'Paga',
      cancelado: 'Cancelada',
      recusado: 'Recusada',
    }[status] ||
    status ||
    'Evento'
  );
}
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <section class="px-2 pb-3 pt-1">
    <div class="rounded-2xl border border-n-weak bg-n-solid-1 p-4">
      <div class="mb-4 flex items-start justify-between gap-3">
        <div>
          <h3 class="mb-1 text-sm font-semibold text-n-slate-12">
            Contexto comercial
          </h3>
          <p class="mb-0 text-xs leading-5 text-n-slate-11">
            Cliente, crédito e linha do tempo em um só lugar.
          </p>
        </div>
        <span
          class="grid size-9 place-items-center rounded-xl bg-n-alpha-2 text-n-brand"
        >
          <span class="i-lucide-briefcase-business size-4" />
        </span>
      </div>

      <div class="grid grid-cols-2 gap-2">
        <div
          v-for="card in contextCards"
          :key="card.label"
          class="min-w-0 rounded-lg border border-n-weak bg-n-solid-2 p-2"
        >
          <span class="mb-2 block size-4 text-n-slate-10" :class="card.icon" />
          <span class="block text-[0.6875rem] font-medium text-n-slate-10">
            {{ card.label }}
          </span>
          <strong class="mt-1 block truncate text-xs text-n-slate-12">
            {{ card.value }}
          </strong>
        </div>
      </div>

      <div
        v-if="visibleAppointments.length"
        class="mt-4"
        data-testid="contact-appointments"
      >
        <div class="mb-2 flex items-center justify-between">
          <h4 class="mb-0 text-xs font-semibold uppercase text-n-slate-10">
            Agendamentos
          </h4>
          <a
            :href="agendaUrl"
            class="text-xs font-medium text-n-blue-text hover:underline"
          >
            Agenda
          </a>
        </div>
        <ul class="m-0 grid list-none gap-2 p-0">
          <li
            v-for="event in visibleAppointments"
            :key="event.id"
            class="flex min-w-0 gap-3 rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2"
            :data-testid="`appointment-${event.id}`"
          >
            <span
              class="mt-0.5 grid size-8 shrink-0 place-items-center rounded-md bg-n-brand/10 text-n-brand"
            >
              <span class="i-hugeicons-calendar-03 size-4" />
            </span>
            <div class="min-w-0 flex-1">
              <div class="flex items-baseline gap-1.5 text-xs">
                <strong class="shrink-0 font-semibold text-n-slate-12">
                  {{ appointmentDay(event) }}
                </strong>
                <span class="truncate text-n-slate-11">
                  {{ appointmentTime(event) }}
                </span>
              </div>
              <div
                class="truncate text-xs text-n-slate-12"
                :title="event.title"
              >
                {{ event.title }}
              </div>
              <div
                class="mt-0.5 flex flex-wrap items-center gap-x-2 text-[0.6875rem] text-n-slate-10"
              >
                <span>{{ appointmentSource(event) }}</span>
                <a
                  v-if="event.metadata?.google_meet_link"
                  :href="event.metadata.google_meet_link"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="inline-flex items-center gap-1 text-[0.6875rem] font-medium text-n-blue-text hover:underline"
                >
                  <span class="i-hugeicons-video-01 size-3" />
                  Google Meet
                </a>
              </div>
              <SendAppointmentButton
                v-if="conversation.id"
                :event="event"
                :conversation="conversation"
                :contact-id="contact.id"
              />
            </div>
          </li>
        </ul>
        <a
          v-if="hiddenAppointments"
          :href="agendaUrl"
          class="mt-2 block text-xs font-medium text-n-blue-text hover:underline"
        >
          {{ `+${hiddenAppointments} agendamento(s) na agenda` }}
        </a>
      </div>

      <div
        v-if="primaryProposal"
        class="mt-4 rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2"
      >
        <span class="block text-[0.6875rem] font-medium text-n-slate-10">
          Proposta em foco
        </span>
        <div class="mt-1 flex items-center justify-between gap-3">
          <span class="min-w-0 truncate text-sm font-semibold text-n-slate-12">
            {{ primaryProposal.title || 'Proposta' }}
          </span>
          <span class="shrink-0 text-sm font-semibold text-n-slate-12">
            {{ formatCurrency(primaryProposal.amount) }}
          </span>
        </div>
        <span class="mt-1 block text-xs text-n-slate-10">
          {{ statusLabel(primaryProposal.status) }}
        </span>
      </div>

      <div class="mt-4">
        <div class="mb-2 flex items-center justify-between">
          <h4 class="mb-0 text-xs font-semibold uppercase text-n-slate-10">
            Timeline do cliente
          </h4>
          <a
            :href="pipelineUrl"
            class="text-xs font-medium text-n-blue-text hover:underline"
          >
            Esteira
          </a>
        </div>

        <div v-if="timeline.length" class="grid gap-3">
          <div
            v-for="item in timeline"
            :key="`${item.label}-${item.date}`"
            class="flex gap-3"
          >
            <span
              class="mt-0.5 grid size-7 shrink-0 place-items-center rounded-full bg-n-alpha-2 text-n-slate-11"
            >
              <span class="size-3.5" :class="item.icon" />
            </span>
            <div class="min-w-0 flex-1">
              <div class="truncate text-xs font-medium text-n-slate-12">
                {{ item.label }}
              </div>
              <div
                class="mt-0.5 flex items-center gap-2 text-[0.6875rem] text-n-slate-10"
              >
                <span>{{ formatDate(item.date) }}</span>
                <span v-if="item.amount">
                  · {{ formatCurrency(item.amount) }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <p
          v-else
          class="mb-0 rounded-lg border border-dashed border-n-weak p-3 text-xs leading-5 text-n-slate-11"
        >
          Ainda não há histórico comercial. Quando houver simulações ou
          propostas, elas aparecem aqui.
        </p>
      </div>

      <div class="mt-4 grid grid-cols-2 gap-2">
        <a
          :href="conversationUrl"
          class="inline-flex h-9 items-center justify-center gap-2 rounded-lg bg-n-alpha-2 px-3 text-xs font-medium text-n-slate-12 hover:bg-n-alpha-3"
        >
          <span class="i-lucide-message-circle size-3.5" />
          Conversa
        </a>
        <a
          :href="pipelineUrl"
          class="inline-flex h-9 items-center justify-center gap-2 rounded-xl bg-n-brand px-3 text-xs font-semibold text-white shadow-sm shadow-n-brand/20 hover:opacity-90"
        >
          <span class="i-lucide-panel-top size-3.5" />
          Esteira
        </a>
      </div>
    </div>
  </section>
</template>
